import { useState } from "react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { Mail, Phone, MapPin, MessageCircle } from "lucide-react";
import SEO from "@/components/common/SEO";
import PageHero from "@/components/common/PageHero";
import ScrollReveal from "@/components/common/ScrollReveal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { FORMS } from "@/constants/testIds";
import { postForm } from "@/lib/api";

const initial = { name: "", email: "", phone: "", company: "", country: "", message: "" };

export default function Contact() {
    const { t } = useTranslation();
    const [form, setForm] = useState(initial);
    const [loading, setLoading] = useState(false);
    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await postForm("/contact", form);
            toast.success(t("toast.contactReceived"));
            setForm(initial);
        } catch { toast.error(t("toast.genericError")); }
        finally { setLoading(false); }
    };
    return (
        <>
            <SEO
                title="Contact Exportfy.world — Global Merchant Exporter"
                description="Contact Exportfy.world for merchant exports, custom sourcing, OEM/private label, and international buyer & supplier services. WhatsApp +91 99180 00070."
                path="/contact"
            />
            <PageHero eyebrow={t("contact.hero.eyebrow")} title={t("contact.hero.title")} subtitle={t("contact.hero.subtitle")} />
            <section className="section-y">
                <div className="container-x grid grid-cols-1 lg:grid-cols-2 gap-14">
                    <ScrollReveal>
                        <div className="space-y-6">
                            <a href="mailto:hello@exportfy.world" className="group flex items-start gap-4 p-6 bg-white border border-slate-200 rounded-2xl hover:border-slate-300 transition">
                                <div className="w-11 h-11 rounded-xl bg-[#0A2540] text-white flex items-center justify-center"><Mail className="w-5 h-5" /></div>
                                <div>
                                    <div className="font-bold text-[#0A2540]">{t("contact.card.emailLabel")}</div>
                                    <div className="text-slate-500 text-sm mt-1">hello@exportfy.world</div>
                                </div>
                            </a>
                            <a href="https://wa.me/919918000070" target="_blank" rel="noopener noreferrer" className="group flex items-start gap-4 p-6 bg-white border border-slate-200 rounded-2xl hover:border-slate-300 transition">
                                <div className="w-11 h-11 rounded-xl bg-[#059669] text-white flex items-center justify-center"><MessageCircle className="w-5 h-5" /></div>
                                <div>
                                    <div className="font-bold text-[#0A2540]">{t("contact.card.whatsappLabel")}</div>
                                    <div className="text-slate-500 text-sm mt-1">+91 99180 00070</div>
                                </div>
                            </a>
                            <div className="flex items-start gap-4 p-6 bg-white border border-slate-200 rounded-2xl">
                                <div className="w-11 h-11 rounded-xl bg-[#1D4ED8] text-white flex items-center justify-center"><Phone className="w-5 h-5" /></div>
                                <div>
                                    <div className="font-bold text-[#0A2540]">{t("contact.card.phoneLabel")}</div>
                                    <div className="text-slate-500 text-sm mt-1">{t("contact.card.phoneValue")}</div>
                                </div>
                            </div>
                            <div className="flex items-start gap-4 p-6 bg-white border border-slate-200 rounded-2xl">
                                <div className="w-11 h-11 rounded-xl bg-[#0A2540] text-white flex items-center justify-center"><MapPin className="w-5 h-5" /></div>
                                <div>
                                    <div className="font-bold text-[#0A2540]">{t("contact.card.officesLabel")}</div>
                                    <div className="text-slate-500 text-sm mt-1">{t("contact.card.officesValue")}</div>
                                </div>
                            </div>
                        </div>
                    </ScrollReveal>

                    <ScrollReveal delay={80}>
                        <form onSubmit={submit} className="bg-white rounded-2xl border border-slate-200 shadow-lg shadow-slate-900/5 p-8">
                            <h2 className="text-2xl font-extrabold text-[#0A2540]">{t("contact.form.title")}</h2>
                            <p className="mt-2 text-sm text-slate-500">{t("contact.form.subtitle")}</p>
                            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <Input required placeholder={t("contact.form.name")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid={FORMS.contact.name} />
                                <Input required type="email" placeholder={t("contact.form.email")} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid={FORMS.contact.email} />
                                <Input placeholder={t("contact.form.phone")} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid={FORMS.contact.phone} />
                                <Input placeholder={t("contact.form.company")} value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} data-testid={FORMS.contact.company} />
                                <Input className="sm:col-span-2" placeholder={t("contact.form.country")} value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} data-testid={FORMS.contact.country} />
                            </div>
                            <Textarea rows={5} required className="mt-4" placeholder={t("contact.form.message")} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid={FORMS.contact.message} />
                            <Button type="submit" disabled={loading} data-testid={FORMS.contact.submit} className="mt-6 rounded-full bg-[#1D4ED8] hover:bg-[#1E40AF] text-white font-semibold px-8 py-6">
                                {loading ? t("contact.form.sending") : t("contact.form.submit")}
                            </Button>
                        </form>
                    </ScrollReveal>
                </div>
            </section>
        </>
    );
}
